/**
 * What a ticket's `source` value means, in words.
 *
 * `Ticket.source` is a free-form column - the API takes any string up to thirty
 * characters - written by whichever door the ticket came in through. The detail
 * card printed it raw, so a message left on the answering machine described
 * itself to the operator as "voicemail": an English word in an Estonian
 * interface, and a column name rather than a sentence.
 *
 * The list is the values the platform actually writes, verified against the six
 * places that create a ticket:
 *   - `crm-support/tickets.routes.ts`        -> "manual" (the default, and what
 *                                               the CRM's own dialog produces)
 *   - `crm-support/public.routes.ts`         -> "form" | "chatbot"
 *   - `crm-email/mail.routes.ts`             -> "email"
 *   - `portal/tickets.routes.ts`             -> "portal"
 *   - `crm-calls/lib/voicemail-records.ts`   -> "voicemail"
 *   - `scripts/perfex-import.ts`             -> "email" (the old helpdesk's rows)
 *
 * Anything else is shown verbatim, for the same reason `leadSource.ts` does it:
 * a missing key rendered as `support.src.x` is worse than the raw value it was
 * meant to replace.
 */

/** Every source value with a translation. */
export const TICKET_SOURCES = ["manual", "form", "chatbot", "email", "portal", "voicemail"] as const;

export type TicketSource = (typeof TICKET_SOURCES)[number];

const KNOWN = new Set<string>(TICKET_SOURCES);

/** The label for a source, or the raw value when nothing here recognises it. */
export function ticketSourceLabel(source: string, t: (key: string) => string): string {
  return KNOWN.has(source) ? t(`support.src.${source}`) : source;
}
